// src/services/authService.js
import api, { baseURL } from "./Api";

// =================== Constantes de permisos ===================
import { BITSET_LEN } from "../constants/permisos";
// Bitset mínimo válido: bit #1 encendido (INICIO_VIEW) y resto 0
const MIN_BITSET = "1" + "0".repeat(BITSET_LEN - 1);

// Normaliza cualquier cadena a 90 bits y fuerza bit #1 = 1
function sanitizeBitset(s) {
  const raw = String(s || "");
  const padded = (raw + "0".repeat(BITSET_LEN)).slice(0, BITSET_LEN);
  return "1" + padded.slice(1); // fuerza bit #1
}

// =================== Util: decodificar payload de JWT (base64url) ===================
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return {};
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // padding
    while (base64.length % 4) base64 += "=";
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// ============================ 2FA: enviar código ============================
export async function enviarCodigo2FA(email, password) {
  try {
    await api.post(`/Usuarios/enviar-token`, { email, password });
    localStorage.setItem("email", email);
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data ||
      err?.message ||
      "Error desconocido";
    throw new Error(String(msg));
  }
}

// ============================ 2FA: verificar código =========================
export async function verificarCodigo2FA(email, codigo2FA) {
  try {
    const { data } = await api.post(`/Usuarios/verificar-2fa`, {
      email,
      codigo2FA,
    }); // { token, email, endpoint, esSuperAdmin?, ... }

    const payload = decodeJwtPayload(data.token);

    const esSuperAdmin =
      typeof payload.esSuperAdmin === "string"
        ? payload.esSuperAdmin === "true"
        : !!payload.esSuperAdmin;

    // Guardar sesión mínima
    localStorage.setItem("token", data.token);
    localStorage.setItem("email", data.email || email);
    if (payload.id != null) localStorage.setItem("id", String(payload.id));
    if (payload.idRol != null) localStorage.setItem("idRol", String(payload.idRol));
    if (payload.rol != null) localStorage.setItem("rol", String(payload.rol));
    localStorage.setItem("esSuperAdmin", String(esSuperAdmin)); // ✅ persistir flag

    // Endpoint/bitset puede venir en data.endpoint (backend) o en el JWT (temporales)
    const rawEndpoint = payload.endpoint || data.endpoint || data.bitset || MIN_BITSET;
    const endpoint = sanitizeBitset(rawEndpoint);
    localStorage.setItem("endpoint", endpoint);

    return {
      token: data.token,
      rol: payload.rol || data.rol || "",
      email: data.email || email,
      endpoint,
      bitset: endpoint, // alias
      esSuperAdmin,     // ✅ útil si lo necesitas inmediatamente
    };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data ||
      err?.message ||
      "Error desconocido";
    throw new Error(String(msg));
  }
}

// ============================ Registro público ==============================
export async function registrarUsuario(usuario) {
  try {
    const { data } = await api.post(`/Usuarios/registro-publico`, usuario);
    return data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data ||
      err?.message ||
      "Error desconocido";
    throw new Error(String(msg));
  }
}

export async function iniciarSesionGoogle(credential, seleccion = {}) {
  try {
    const { data } = await api.post("/Usuarios/google", { credential, ...seleccion });
    return data;
  } catch (err) {
    const body = err?.response?.data;
    throw new Error(typeof body === "string" ? body : body?.mensaje || "No fue posible iniciar sesión con Google.");
  }
}

export const listarCuentas = async () => (await api.get("/Usuarios/cuentas")).data;
export const cambiarCuenta = async (perfilId) =>
  (await api.post(`/Usuarios/cambiar-cuenta/${perfilId}`)).data;
export const crearCuentaPrincipal = async () =>
  (await api.post("/Usuarios/crear-cuenta-principal")).data;

// ============================ Utilidades sesión =============================
export function getToken() {
  return localStorage.getItem("token");
}

export function logout() {
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("id");
  localStorage.removeItem("idRol");
  localStorage.removeItem("rol");
  localStorage.removeItem("endpoint");
  localStorage.removeItem("esSuperAdmin"); // ✅ limpiar también
}

// (opcional para debug)
export const __DEBUG_API_BASE__ = `${baseURL}/usuarios`;
